import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PollCard from '../components/PollCard';
import { GoogleSignInButton, AppleSignInButton } from '../App';
import { useAuth } from '../lib/auth';
import { VotoLetters, Wordmark } from '../components/Chrome';
import StoreBadges from '../components/StoreBadges';

const API_BASE = process.env.REACT_APP_API_BASE || '/api';
const HERO_FEATURED = 5;

// The one line of copy (spec §4: full modernization, Eric 10-09).
export const LANDING_LINE = 'Ask your group with photos. They vote with one tap. No sign-in, no app.';

async function getJson(path, signal) {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include', signal });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.json();
}

// The hero: Poll of the Day first, then the editorially featured archive.
// curated=true keeps the public marketing page to the admin-curated,
// App-Store-safe set (it is reviewed as metadata via the marketing URL,
// guideline 4.1(a)); sort=featured (not popular) keeps the all-time vote
// leaders — some of them third-party logo polls — off this page.
function HeroPolls() {
  const [polls, setPolls] = useState(null);
  const [potdId, setPotdId] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const potd = getJson('/polls/potd', controller.signal).then((d) => (d?.potd ? [d.potd] : [])).catch(() => []);
    const featured = getJson(`/polls?curated=true&sort=featured&limit=${HERO_FEATURED}`, controller.signal)
      .then((d) => (Array.isArray(d) ? d : []))
      .catch(() => []);
    Promise.all([potd, featured]).then(([lead, rest]) => {
      if (controller.signal.aborted) return;
      const seen = new Set(lead.map((p) => p.id));
      setPotdId(lead[0]?.id ?? null);
      setPolls([...lead, ...rest.filter((p) => !seen.has(p.id))].slice(0, 1 + HERO_FEATURED));
    });
    return () => controller.abort();
  }, []);

  if (polls === null) {
    // Hold the space with tile-shaped placeholders so the page doesn't jump.
    return (
      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3" aria-hidden="true">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className={`grid aspect-square grid-cols-2 gap-[2px] ${i > 0 ? 'hidden sm:grid' : ''}`}>
            {Array.from({ length: 4 }).map((__, j) => <div key={j} className="animate-pulse bg-voto-surface" />)}
          </div>
        ))}
      </div>
    );
  }
  if (!polls.length) return null;

  return (
    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3" data-testid="landing-polls">
      {polls.map((poll) => (
        <PollCard
          key={poll.id}
          poll={poll}
          to={`/poll/${poll.id}`}
          eyebrow={poll.id === potdId ? 'Poll of the day' : null}
        />
      ))}
    </div>
  );
}

export default function Landing() {
  const { user, googleLogin, appleLogin } = useAuth() || {};
  const navigate = useNavigate();

  // Start the OAuth flow straight from the landing button (no detour via /auth).
  async function handleGoogle(credential) {
    try { await googleLogin(credential); navigate('/'); } catch (_) { /* surfaced on /auth if it fails */ }
  }
  async function handleApple(identityToken, name) {
    try { await appleLogin(identityToken, name); navigate('/'); } catch (_) { /* no-op on cancel */ }
  }

  return (
    <div className="flex min-h-screen flex-col bg-voto-bg text-voto-fg">
      <header className="bg-voto-chrome">
        <div className="mx-auto flex w-full max-w-6xl items-center gap-3 px-4 pb-3 pt-3.5 sm:px-6">
          <Wordmark />
          <span className="flex-1" />
          <Link to="/feed" className="rounded-md px-2 py-1 text-sm font-medium text-voto-muted transition hover:text-white">
            Feed
          </Link>
          {user ? (
            <Link to="/feed" className="rounded-[10px] bg-voto-coral px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-voto-coralDeep">
              Open Voto
            </Link>
          ) : (
            <Link to="/auth" className="rounded-[10px] bg-voto-coral px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-voto-coralDeep">
              Sign in
            </Link>
          )}
        </div>
      </header>

      <main className="flex-1">
        {/* Hero: one line, then the live polls themselves. */}
        <section className="mx-auto w-full max-w-6xl pb-10 pt-8 sm:px-6 sm:pt-12">
          <div className="px-4 sm:px-0">
            <h1 className="max-w-2xl text-[28px] font-semibold leading-[1.15] tracking-[-0.02em] text-white sm:text-[40px]">
              {LANDING_LINE}
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/polls/new"
                className="rounded-[14px] bg-voto-coral px-5 py-3 text-[15px] font-semibold text-white transition hover:bg-voto-coralDeep"
              >
                Ask your group
              </Link>
              <Link
                to="/feed"
                className="rounded-[14px] bg-voto-surface px-5 py-3 text-[15px] font-semibold text-voto-fg ring-1 ring-voto-line transition hover:bg-voto-raised"
              >
                Vote on the feed
              </Link>
            </div>
            <h2 className="mb-3 mt-10 text-sm font-semibold text-voto-coral">On Voto</h2>
          </div>
          <HeroPolls />
        </section>

        {/* Secondary: sign in and the apps. */}
        <section className="border-t border-voto-line bg-voto-chrome/40">
          <div className="mx-auto grid w-full max-w-6xl gap-6 px-4 py-10 sm:grid-cols-2 sm:px-6">
            {!user && (
              <div>
                <h2 className="text-base font-semibold text-white">Keep your polls and credits</h2>
                <p className="mt-1 text-sm text-voto-muted">Sign in to ask, see who voted, and earn credits in the Voting Booth.</p>
                <div className="mt-4 flex max-w-xs flex-col gap-3">
                  <GoogleSignInButton onCredential={handleGoogle} />
                  <AppleSignInButton onCredential={handleApple} />
                  <Link to="/auth" className="rounded text-sm font-semibold text-voto-coral hover:underline">
                    Sign in with email
                  </Link>
                </div>
              </div>
            )}
            <div>
              <h2 className="text-base font-semibold text-white">Get the app</h2>
              <p className="mt-1 text-sm text-voto-muted">Voto on iPhone and Android. Your account works everywhere.</p>
              <StoreBadges className="mt-4" />
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-voto-line bg-voto-bg py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-5 px-4 sm:flex-row sm:justify-between sm:px-6">
          <span className="font-voto text-base font-bold tracking-[-0.02em] text-voto-muted" aria-label="votomoto" role="img"><VotoLetters /></span>
          <nav aria-label="Footer" className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-voto-muted">
            <Link to="/tags" className="rounded transition hover:text-white">Tags</Link>
            <a href="mailto:eric@ericstevenson.com" className="rounded transition hover:text-white">Contact</a>
            <Link to="/privacy" className="rounded transition hover:text-white">Privacy</Link>
            <Link to="/terms" className="rounded transition hover:text-white">Terms</Link>
          </nav>
        </div>
        <p className="mx-auto mt-6 max-w-6xl px-4 text-center text-xs text-voto-muted sm:px-6">
          Apple and the App Store are trademarks of Apple Inc. Google Play and the Google Play logo
          are trademarks of Google LLC.
        </p>
      </footer>
    </div>
  );
}
